import dayjs from 'dayjs';
import type { Facility, FacilityCertificate, Supplier } from 'features/suppliers/types';
import { facilitiesDataCache, type MockFacility } from 'mocks/api/handlers/facilitiesCache';
import { getSuppliersData } from 'mocks/api/handlers/suppliersCache';

/**
 * Deterministic, additive seed for DL-65103 (Testmo `Brand Certificate View` folder,
 * TC-BV-002 / DL-65104) — the brand-side counterpart to DL-65097's five-state status matrix.
 *
 * DL-65097's own `certificateStatusLifecycleFixture.ts` (sub-supplier `certsl`) is the fixture
 * this story's acceptance criteria ask to reuse, but that epic's PR (#14876) is still open,
 * unmerged, and blocked on human review approval — its fixture module does not exist on `develop`
 * yet, so it cannot be imported here without depending on unmerged code. This module reproduces
 * the SAME five-state date/status pattern (not yet valid / valid / expiring soon / expired /
 * inactive, with the inactive certificate deliberately sharing the expired certificate's dates to
 * prove the precedence fallback) under its own, distinctly-prefixed ids (`cbv-*`) so this epic's
 * PR is self-contained and does not collide with #14876's fixture once it lands.
 *
 * Reached via a BRAND session (the default `page` fixture, org 2) viewing a direct supplier's
 * SUBSUPPLIER — `SupplierFacilities`/`FacilityCertificates` (`isForDirectSupplier`) at
 * `/direct-suppliers/:directSupplierId/full-details/subsuppliers/:supplierId/:supplierOrgId/facilities`.
 * That component tree is genuinely distinct from `AllFacilitiesTab` (the epic's own prerequisite
 * guess): `AllFacilitiesTab` never renders `FacilityCertificates` for either role — only
 * `SupplierFacilities` does (confirmed by direct code read, not assumed). `directSupplierId` is
 * never used for a data fetch on this route (only for the back-button and a segment-event field),
 * so it does not need to resolve to a real seeded direct supplier — any path segment works.
 *
 * Two independent lookups need this same supplier record, keyed by two DIFFERENT org ids (a
 * quirk of the shipped endpoints, confirmed by reading both handlers, not assumed):
 *  - the nested facilities/certificates GET routes are keyed ONLY by `subsupplierId` — the
 *    `:orgId` path segment is read but never used to look up data — so `supplierOrgId` can be any
 *    distinct numeric string;
 *  - `useGetSupplier` (the page header / `SupplierDetails`) calls
 *    `GET /api/organizations/{currentOrgId}/sub-suppliers/{supplierId}/{supplierOrgId}/details`,
 *    where `currentOrgId` is the LOGGED-IN brand session's own org (`useOrgId()` = 2 for the
 *    default `page` fixture, per `mocks/data/user.ts`), so the supplier record must additionally
 *    be registered under `getSuppliersData(2)`, not just under the subsupplier's own org id.
 */

/** Stable ids used across list -> detail -> facilities -> certificates and in `data-testid`s. */
export const BRAND_VIEW_FIXTURE = {
  /** Path segment only — never resolved against real direct-supplier data (see module doc). */
  directSupplierId: 'cbv-direct',
  subSupplierId: 'cbv-sub',
  subSupplierName: 'Certificate Brand View Subsupplier',
  subSupplierOrgId: '77001',
  facilityId: 'cbv-facility',
  certs: {
    notYetValid: 'cbv-not-yet-valid',
    valid: 'cbv-valid',
    expiringSoon: 'cbv-expiring-soon',
    expired: 'cbv-expired',
    inactive: 'cbv-inactive',
  },
} as const;

/** The brand session's own org id (`loggedInUser.OrganizationId`, `mocks/data/user.ts`). */
const BRAND_ORG_ID = 2;

const today = dayjs().startOf('day');
const iso = (d: ReturnType<typeof dayjs>) => d.toISOString();

let standardIdSeq = 971_100;
let instituteIdSeq = 971_500;

const makeCert = (
  id: string,
  standardName: string,
  status: string,
  validFrom: string,
  validTo: string
): FacilityCertificate => ({
  id,
  certificateNumber: `CERT-${id.toUpperCase()}`,
  standard: {
    id: standardIdSeq++,
    name: standardName,
    certificateType: 'facility',
  },
  institute: {
    id: instituteIdSeq++,
    name: 'Brand View Fixture Institute',
  },
  // The declared `FacilityCertificateStatus` union predates the real API status enum (see the
  // sibling lazy-load fixture's identical note) — cast the same way the existing handlers do.
  status: status as FacilityCertificate['status'],
  validFrom,
  validTo,
});

const buildCertificates = (): FacilityCertificate[] => {
  const c = BRAND_VIEW_FIXTURE.certs;
  return [
    makeCert(
      c.notYetValid,
      'BCI',
      'not_yet_valid',
      iso(today.add(30, 'day')),
      iso(today.add(500, 'day'))
    ),
    makeCert(c.valid, 'GOTS', 'valid', iso(today.subtract(100, 'day')), iso(today.add(300, 'day'))),
    makeCert(
      c.expiringSoon,
      'OEKO-TEX Standard 100',
      'expiring_soon',
      iso(today.subtract(300, 'day')),
      iso(today.add(30, 'day'))
    ),
    makeCert(
      c.expired,
      'GRS',
      'expired',
      iso(today.subtract(600, 'day')),
      iso(today.subtract(100, 'day'))
    ),
    // Deliberate collision: same dates as `expired`, proving the badge falls back to the
    // date-computed "Expired" label rather than rendering a literal "Inactive" (DL-65099 owns
    // the precedence rule itself; this story only asserts the rendered badge).
    makeCert(
      c.inactive,
      'RCS',
      'inactive',
      iso(today.subtract(600, 'day')),
      iso(today.subtract(100, 'day'))
    ),
  ];
};

const makeFacility = (certificates: FacilityCertificate[]): MockFacility => {
  const base: Facility = {
    id: BRAND_VIEW_FIXTURE.facilityId,
    organizationId: Number(BRAND_VIEW_FIXTURE.subSupplierOrgId),
    organizationSubSupplierId: BRAND_VIEW_FIXTURE.subSupplierId,
    name: 'Brand View Fixture Facility',
    address: '5 Brand View Way',
    zipCode: '4000',
    city: 'Aarhus',
    stateOrRegion: 'Central Denmark',
    countryCode: 'DK',
    latitude: 56.1629,
    longitude: 10.2039,
    website: 'https://example.com',
    vatNumber: 'DK33333333',
    mid: 'MID-CBVFIX',
    relationType: 1,
    processes: [],
    isArchived: false,
    isInUse: true,
    certificateCount: certificates.length,
  };
  return { ...base, facilityCertificates: certificates };
};

const makeSupplier = (): Supplier => ({
  id: BRAND_VIEW_FIXTURE.subSupplierId,
  organizationId: Number(BRAND_VIEW_FIXTURE.subSupplierOrgId),
  name: BRAND_VIEW_FIXTURE.subSupplierName,
  countryCode: 'DK',
  city: 'Aarhus',
  address: '5 Brand View Way',
  relationType: 1,
  avatarColor: '#4a6fa5',
  archived: false,
  isArchived: false,
  isSupplierInUse: true,
  contacts: [],
  facilities: [],
});

let seeded = false;

/**
 * Seed the deterministic brand-view certificate status fixture. Idempotent and additive: it
 * registers one sub-supplier record under BOTH `getSuppliersData(2)` (the brand session's own
 * org — needed by the `SupplierDetails` header lookup) and its own facilities-cache key (needed
 * by the facilities list + nested certificates table), and never mutates any other epic's seed.
 */
export const seedCertificateStatusBrandViewFixture = (): void => {
  if (seeded) return;
  seeded = true;

  const supplier = makeSupplier();

  const brandOrgSuppliers = getSuppliersData(BRAND_ORG_ID);
  if (!brandOrgSuppliers.some(s => s.id === BRAND_VIEW_FIXTURE.subSupplierId)) {
    brandOrgSuppliers.unshift(supplier);
  }

  facilitiesDataCache.set(BRAND_VIEW_FIXTURE.subSupplierId, [makeFacility(buildCertificates())]);
};
